import { collection, doc, getDoc, getDocs, query, where } from "firebase/firestore";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/auth/AuthProvider";
import { DynamicFieldInput } from "@/components/DynamicFieldInput";
import { ErrorText, LoadingView, PrimaryButton, SBCard, SecondaryButton, SportsBackground } from "@/components/ui";
import { db } from "@/firebase/app";
import { parseLeague, parseSport, type LeagueModel, type SportModel } from "@/models/catalog";
import type { LeagueDynamicFieldValue } from "@/models/dynamicFieldValue";
import { schemaActiveFields, parseFieldSchema, organizationAdminValueID, type LeagueFieldSchema, type LeagueOrganizationAdminValue } from "@/models/schema";
import type { OrganizationModel } from "@/models/organization";
import { inviteTargetDisplayName, previewInvite, redeemInvite, INVITE_ROLE_LABELS, type SBotInviteSummary } from "@/services/invites";
import { completeOnboarding, customLeague, customOrganization, customSport } from "@/services/onboarding";
import { syncCurrentUserMemberSummaries } from "@/services/account";
import "./onboarding.css";

// Port of Views/OnboardingView.swift: intent → sport → league → organization →
// admin data → review. The invite path previews the code and jumps ahead.

type OnboardingStep = "intent" | "sport" | "league" | "organization" | "adminData" | "review";
type OnboardingIntent = "createOrganization" | "joinWithInvite";
type OrganizationMode = "organization" | "leagueOnly";

const STEP_TITLES: Record<OnboardingStep, string> = {
  intent: "Welcome to SBot",
  sport: "Choose your sport",
  league: "Choose your league",
  organization: "Your organization",
  adminData: "Organization admin details",
  review: "Review & finish",
};

export function OnboardingView() {
  const { currentUser, refreshWorkspaceOptions } = useAuth();
  const navigate = useNavigate();

  const [step, setStep] = useState<OnboardingStep>("intent");
  const [intent, setIntent] = useState<OnboardingIntent>("createOrganization");
  const [inviteCode, setInviteCode] = useState("");
  const [invitePreview, setInvitePreview] = useState<SBotInviteSummary | null>(null);
  const [isLoadingInvite, setIsLoadingInvite] = useState(false);

  const [sports, setSports] = useState<SportModel[]>([]);
  const [isLoadingSports, setIsLoadingSports] = useState(false);
  const [selectedSport, setSelectedSport] = useState<SportModel | null>(null);
  const [shouldSaveSport, setShouldSaveSport] = useState(false);
  const [customSportName, setCustomSportName] = useState("");

  const [leagues, setLeagues] = useState<LeagueModel[]>([]);
  const [isLoadingLeagues, setIsLoadingLeagues] = useState(false);
  const [selectedLeague, setSelectedLeague] = useState<LeagueModel | null>(null);
  const [shouldSaveLeague, setShouldSaveLeague] = useState(false);
  const [customLeagueName, setCustomLeagueName] = useState("");
  const [customLeagueAbbreviation, setCustomLeagueAbbreviation] = useState("");

  const [organizationMode, setOrganizationMode] = useState<OrganizationMode>("organization");
  const [organizationName, setOrganizationName] = useState("");
  const [organizationAbbreviation, setOrganizationAbbreviation] = useState("");
  const [organizationLocation, setOrganizationLocation] = useState("");

  const [adminSchema, setAdminSchema] = useState<LeagueFieldSchema | null>(null);
  const [adminValues, setAdminValues] = useState<Record<string, LeagueDynamicFieldValue>>({});

  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const userID = currentUser?.id;

  useEffect(() => {
    if (step !== "sport" || sports.length > 0) return;
    setIsLoadingSports(true);
    void getDocs(collection(db, "sports"))
      .then((snapshot) => {
        const loaded = snapshot.docs
          .map((docSnapshot) => parseSport(docSnapshot.id, docSnapshot.data()))
          .filter((sport): sport is SportModel => sport !== null)
          .filter((sport) => sport.reviewStatus !== "rejected")
          .sort((a, b) => a.name.localeCompare(b.name));
        setSports(loaded);
      })
      .catch((error: unknown) => setErrorMessage(error instanceof Error ? error.message : "Sports could not be loaded."))
      .finally(() => setIsLoadingSports(false));
  }, [step, sports.length]);

  const loadLeagues = async (sport: SportModel) => {
    setIsLoadingLeagues(true);
    setLeagues([]);
    try {
      const snapshot = await getDocs(query(collection(db, "leagues"), where("sportID", "==", sport.id)));
      setLeagues(
        snapshot.docs
          .map((docSnapshot) => parseLeague(docSnapshot.id, docSnapshot.data()))
          .filter((league): league is LeagueModel => league !== null)
          .filter((league) => league.reviewStatus !== "rejected")
          .sort((a, b) => a.name.localeCompare(b.name)),
      );
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : "Leagues could not be loaded.");
    } finally {
      setIsLoadingLeagues(false);
    }
  };

  const selectSport = (sport: SportModel, save: boolean) => {
    setSelectedSport(sport);
    setShouldSaveSport(save);
    setSelectedLeague(null);
    setLeagues([]);
    setAdminSchema(null);
    setAdminValues({});
    void loadLeagues(sport);
  };

  const selectLeague = (league: LeagueModel, save: boolean) => {
    setSelectedLeague(league);
    setShouldSaveLeague(save);
    setAdminSchema(null);
    setAdminValues({});
  };

  const loadAdminSchema = async (leagueID: string) => {
    try {
      const snapshot = await getDoc(doc(db, "leagueOrganizationAdminSchemas", leagueID));
      setAdminSchema(snapshot.exists() ? parseFieldSchema(snapshot.id, snapshot.data()) : null);
    } catch {
      setAdminSchema(null);
    }
  };

  const finalOrganization = useMemo((): OrganizationModel | null => {
    if (!selectedSport || !selectedLeague || organizationMode !== "organization" || !userID) return null;
    const name = organizationName.trim();
    if (!name) return null;
    return customOrganization(selectedSport.id, selectedLeague.id, name, organizationAbbreviation, organizationLocation, userID);
  }, [selectedSport, selectedLeague, organizationMode, organizationName, organizationAbbreviation, organizationLocation, userID]);

  const adminFields = useMemo(() => schemaActiveFields(adminSchema), [adminSchema]);

  const buildAdminValue = (): LeagueOrganizationAdminValue | null => {
    if (!currentUser) return null;
    const leagueID = intent === "joinWithInvite" ? invitePreview?.leagueID : selectedLeague?.id;
    const organizationID = intent === "joinWithInvite" ? invitePreview?.organizationID : finalOrganization?.id;
    if (!leagueID || !organizationID || adminFields.length === 0) return null;

    const publicValues: Record<string, LeagueDynamicFieldValue> = {};
    const organizationValues: Record<string, LeagueDynamicFieldValue> = {};
    const leagueValues: Record<string, LeagueDynamicFieldValue> = {};
    for (const field of adminFields) {
      const value = adminValues[field.id];
      if (!value) continue;
      if (field.privacyScope === "public") publicValues[field.id] = value;
      else if (field.privacyScope === "organization") organizationValues[field.id] = value;
      else leagueValues[field.id] = value;
    }
    const now = new Date();
    return {
      id: organizationAdminValueID(leagueID, organizationID, currentUser.id),
      leagueID,
      organizationID,
      adminUserID: currentUser.id,
      publicValues,
      organizationValues,
      leagueValues,
      createdAt: now,
      updatedAt: now,
    };
  };

  const missingRequiredAdminFields = adminFields.filter((field) => field.isRequired && !adminValues[field.id]);

  const previewInviteAndAdvance = async () => {
    const code = inviteCode.trim();
    if (!code) return;
    setIsLoadingInvite(true);
    setErrorMessage(null);
    setInvitePreview(null);
    try {
      const preview = await previewInvite(code);
      setInvitePreview(preview);
      if (preview.role === "organizationAdmin" && preview.organizationID) {
        await loadAdminSchema(preview.leagueID);
      }
      setStep("review");
    } catch (error) {
      setErrorMessage(error instanceof Error ? `Invite could not be loaded. ${error.message}` : "Invite could not be loaded.");
    } finally {
      setIsLoadingInvite(false);
    }
  };

  const finish = async () => {
    if (!currentUser) {
      setErrorMessage("User profile is not loaded.");
      return;
    }
    setIsSaving(true);
    setErrorMessage(null);
    try {
      if (intent === "joinWithInvite") {
        await redeemInvite(inviteCode.trim(), true, buildAdminValue() ?? undefined);
        await syncCurrentUserMemberSummaries().catch(() => undefined);
      } else {
        if (!selectedSport || !selectedLeague) {
          setErrorMessage("Choose a sport and league before finishing.");
          return;
        }
        await completeOnboarding({
          user: currentUser,
          role: "admin",
          sport: selectedSport,
          shouldSaveSport,
          league: selectedLeague,
          shouldSaveLeague,
          organization: finalOrganization,
          shouldSaveOrganization: finalOrganization !== null,
          organizationAdminValue: buildAdminValue(),
          inviteCode: null,
        });
      }
      await refreshWorkspaceOptions();
      // The users/{uid} listener picks up onboardingStatus=complete; navigate home.
      navigate("/", { replace: true });
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : "Onboarding could not be completed.");
    } finally {
      setIsSaving(false);
    }
  };

  const canContinue = (() => {
    switch (step) {
      case "intent":
        return intent === "createOrganization" || (inviteCode.trim().length > 0 && !isLoadingInvite);
      case "sport":
        return selectedSport !== null;
      case "league":
        return selectedLeague !== null;
      case "organization":
        return organizationMode === "leagueOnly" || finalOrganization !== null;
      case "adminData":
        return missingRequiredAdminFields.length === 0;
      case "review":
        return !isSaving;
    }
  })();

  const advance = () => {
    setErrorMessage(null);
    switch (step) {
      case "intent":
        if (intent === "joinWithInvite") {
          void previewInviteAndAdvance();
        } else {
          setStep("sport");
        }
        break;
      case "sport":
        setStep("league");
        break;
      case "league":
        setStep("organization");
        break;
      case "organization":
        if (selectedLeague) void loadAdminSchema(selectedLeague.id);
        setStep(finalOrganization ? "adminData" : "review");
        break;
      case "adminData":
        setStep("review");
        break;
      case "review":
        void finish();
        break;
    }
  };

  const back = () => {
    setErrorMessage(null);
    const order: OnboardingStep[] =
      intent === "joinWithInvite" ? ["intent", "review"] : ["intent", "sport", "league", "organization", "adminData", "review"];
    const index = order.indexOf(step);
    if (index > 0) {
      let target = order[index - 1];
      if (target === "adminData" && adminFields.length === 0) target = "organization";
      setStep(target);
    }
  };

  return (
    <SportsBackground>
      <div className="onboarding-scroll">
        <header className="onboarding-header">
          <h1>{STEP_TITLES[step]}</h1>
          <p>Set up your workspace so competitions, athletes, and GPS tools land in the right place.</p>
        </header>

        <div className="onboarding-body">
          {step === "intent" && (
            <SBCard>
              <div className="onboarding-options">
                <button
                  type="button"
                  className={`onboarding-option${intent === "createOrganization" ? " selected" : ""}`}
                  onClick={() => setIntent("createOrganization")}
                >
                  <strong>Create an organization</strong>
                  <span>Start a league workspace you administer.</span>
                </button>
                <button
                  type="button"
                  className={`onboarding-option${intent === "joinWithInvite" ? " selected" : ""}`}
                  onClick={() => setIntent("joinWithInvite")}
                >
                  <strong>Join with invite code</strong>
                  <span>Use a code from a league or organization admin.</span>
                </button>
                {intent === "joinWithInvite" && (
                  <input
                    placeholder="Invite code (ABCD2345)"
                    value={inviteCode}
                    onChange={(e) => setInviteCode(e.target.value.toUpperCase())}
                  />
                )}
                {isLoadingInvite && <LoadingView label="Checking invite…" />}
              </div>
            </SBCard>
          )}

          {step === "sport" && (
            <>
              <SBCard>
                <h3>Create a sport</h3>
                <p className="onboarding-hint">Use this when SBot Labs does not already list the sport you organize.</p>
                <div className="onboarding-inline-form">
                  <input placeholder="Sport name (Triathlon)" value={customSportName} onChange={(e) => setCustomSportName(e.target.value)} />
                  <SecondaryButton
                    title="Use new sport"
                    disabled={!customSportName.trim() || !userID}
                    onClick={() => userID && selectSport(customSport(customSportName, userID), true)}
                  />
                </div>
              </SBCard>
              <SBCard>
                <h3>Choose an existing sport</h3>
                {isLoadingSports ? (
                  <LoadingView label="Loading sports…" />
                ) : sports.length === 0 ? (
                  <p className="onboarding-hint">No sports are available yet.</p>
                ) : (
                  <div className="onboarding-options">
                    {sports.map((sport) => (
                      <button
                        key={sport.id}
                        type="button"
                        className={`onboarding-option${selectedSport?.id === sport.id ? " selected" : ""}`}
                        onClick={() => selectSport(sport, false)}
                      >
                        <strong>{sport.name}</strong>
                        <span>
                          {sport.source === "espn" ? "ESPN" : "Custom"} · {sport.reviewStatus}
                        </span>
                      </button>
                    ))}
                  </div>
                )}
              </SBCard>
            </>
          )}

          {step === "league" && (
            <>
              <SBCard>
                <h3>Create a league</h3>
                <div className="onboarding-inline-form">
                  <input placeholder="League name" value={customLeagueName} onChange={(e) => setCustomLeagueName(e.target.value)} />
                  <input
                    placeholder="Abbreviation"
                    value={customLeagueAbbreviation}
                    onChange={(e) => setCustomLeagueAbbreviation(e.target.value)}
                    style={{ maxWidth: 140 }}
                  />
                  <SecondaryButton
                    title="Use new league"
                    disabled={!customLeagueName.trim() || !selectedSport || !userID}
                    onClick={() =>
                      selectedSport &&
                      userID &&
                      selectLeague(customLeague(selectedSport.id, customLeagueName, customLeagueAbbreviation, userID), true)
                    }
                  />
                </div>
              </SBCard>
              <SBCard>
                <h3>Choose an existing league</h3>
                {isLoadingLeagues ? (
                  <LoadingView label="Loading leagues…" />
                ) : leagues.length === 0 ? (
                  <p className="onboarding-hint">No leagues exist for {selectedSport?.name ?? "this sport"} yet.</p>
                ) : (
                  <div className="onboarding-options">
                    {leagues.map((league) => (
                      <button
                        key={league.id}
                        type="button"
                        className={`onboarding-option${selectedLeague?.id === league.id ? " selected" : ""}`}
                        onClick={() => selectLeague(league, false)}
                      >
                        <strong>{league.name}</strong>
                        <span>
                          {league.abbreviation || league.slug} · {league.reviewStatus}
                        </span>
                      </button>
                    ))}
                  </div>
                )}
              </SBCard>
            </>
          )}

          {step === "organization" && (
            <SBCard>
              <div className="onboarding-options">
                <button
                  type="button"
                  className={`onboarding-option${organizationMode === "organization" ? " selected" : ""}`}
                  onClick={() => setOrganizationMode("organization")}
                >
                  <strong>Create an organization</strong>
                  <span>A team, club, or stable inside {selectedLeague?.name ?? "the league"}.</span>
                </button>
                <button
                  type="button"
                  className={`onboarding-option${organizationMode === "leagueOnly" ? " selected" : ""}`}
                  onClick={() => setOrganizationMode("leagueOnly")}
                >
                  <strong>League only</strong>
                  <span>Administer the league without creating an organization.</span>
                </button>
              </div>
              {organizationMode === "organization" && (
                <div className="onboarding-stack">
                  <input placeholder="Organization name" value={organizationName} onChange={(e) => setOrganizationName(e.target.value)} />
                  <div className="onboarding-inline-form">
                    <input
                      placeholder="Abbreviation"
                      value={organizationAbbreviation}
                      onChange={(e) => setOrganizationAbbreviation(e.target.value)}
                    />
                    <input placeholder="Location" value={organizationLocation} onChange={(e) => setOrganizationLocation(e.target.value)} />
                  </div>
                </div>
              )}
            </SBCard>
          )}

          {step === "adminData" && (
            <SBCard>
              <h3>Organization admin details</h3>
              {adminFields.length === 0 ? (
                <p className="onboarding-hint">This league does not require extra admin details.</p>
              ) : (
                <div className="onboarding-stack">
                  {adminFields.map((field) => (
                    <DynamicFieldInput
                      key={field.id}
                      field={field}
                      value={adminValues[field.id]}
                      onChange={(value) =>
                        setAdminValues((previous) => {
                          const next = { ...previous };
                          if (value === undefined) delete next[field.id];
                          else next[field.id] = value;
                          return next;
                        })
                      }
                    />
                  ))}
                </div>
              )}
            </SBCard>
          )}

          {step === "review" && (
            <SBCard>
              <div className="onboarding-summary">
                {intent === "joinWithInvite" && invitePreview ? (
                  <>
                    <SummaryRow label="Invite" value={`${INVITE_ROLE_LABELS[invitePreview.role]} / ${inviteTargetDisplayName(invitePreview)}`} />
                    <SummaryRow label="League" value={invitePreview.leagueName} />
                    <SummaryRow label="Invited by" value={invitePreview.inviterName} />
                  </>
                ) : (
                  <>
                    <SummaryRow label="Sport" value={selectedSport?.name ?? "Missing"} />
                    <SummaryRow label="League" value={selectedLeague?.name ?? "Missing"} />
                    <SummaryRow
                      label="Organization"
                      value={organizationMode === "leagueOnly" ? "League only" : (finalOrganization?.name ?? "Missing")}
                    />
                    <SummaryRow label="Your role" value="Admin" />
                  </>
                )}
                {intent === "joinWithInvite" && adminFields.length > 0 && (
                  <div className="onboarding-stack" style={{ marginTop: 12 }}>
                    {adminFields.map((field) => (
                      <DynamicFieldInput
                        key={field.id}
                        field={field}
                        value={adminValues[field.id]}
                        onChange={(value) =>
                          setAdminValues((previous) => {
                            const next = { ...previous };
                            if (value === undefined) delete next[field.id];
                            else next[field.id] = value;
                            return next;
                          })
                        }
                      />
                    ))}
                  </div>
                )}
              </div>
            </SBCard>
          )}

          <ErrorText>{errorMessage}</ErrorText>

          <div className="onboarding-nav">
            {step !== "intent" && <SecondaryButton title="Back" onClick={back} disabled={isSaving} />}
            <PrimaryButton
              title={step === "review" ? (isSaving ? "Finishing…" : "Finish") : "Continue"}
              onClick={advance}
              disabled={!canContinue || isSaving}
            />
          </div>
        </div>
      </div>
    </SportsBackground>
  );
}

function SummaryRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="onboarding-summary-row">
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}
